import { jwtDecode } from 'jwt-decode';
import config from '../config';

const REFRESH_LEAD_SECONDS = 60;

const tokenExp = (token) => {
  try {
    const exp = jwtDecode(token).exp;
    return typeof exp === 'number' ? exp : null;
  } catch {
    return null;
  }
};

const secondsRemaining = (token) => {
  const exp = tokenExp(token);
  if (!exp) return Infinity;
  return exp - Date.now() / 1000;
};

export const auth = {
  _refreshPromise: null,
  _refreshTimer: null,

  async login(email, password) {
    try {
      const response = await fetch(`${config.apiBaseUrl}/api/v1/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password
        })
      });
      const data = await response.json();
      
      if (data.success && data.token) {
        localStorage.setItem('token', data.token);
        this.scheduleRefresh();
        return true;
      }
      return false;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  },

  // Extend the current login token via POST /api/v1/user-token-refresh.
  // The login endpoint itself cannot lengthen expiry.
  async refreshSession() {
    if (this._refreshPromise) return this._refreshPromise;
    const current = localStorage.getItem('token');
    if (!current) return false;

    this._refreshPromise = (async () => {
      try {
        const response = await fetch(`${config.apiBaseUrl}/api/v1/user-token-refresh`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${current}`,
          },
          body: '{}',
        });
        const data = await response.json().catch(() => null);
        if (!response.ok || !data?.success || typeof data.token !== 'string' || !data.token) {
          return false;
        }
        if (localStorage.getItem('token') !== current) return false;
        localStorage.setItem('token', data.token);
        this.scheduleRefresh();
        return true;
      } catch (error) {
        console.error('Token refresh error:', error);
        return false;
      } finally {
        this._refreshPromise = null;
      }
    })();

    return this._refreshPromise;
  },

  // Refresh once the token is inside the lead window, including just after expiry.
  async ensureFreshToken() {
    const token = localStorage.getItem('token');
    if (!token) return null;
    if (secondsRemaining(token) > REFRESH_LEAD_SECONDS) return token;
    const refreshed = await this.refreshSession();
    if (refreshed) return localStorage.getItem('token');
    if (secondsRemaining(token) > 0) return token;
    return null;
  },

  scheduleRefresh() {
    if (this._refreshTimer) {
      clearTimeout(this._refreshTimer);
      this._refreshTimer = null;
    }
    const token = localStorage.getItem('token');
    if (!token) return;
    const remaining = secondsRemaining(token);
    if (!Number.isFinite(remaining)) return;
    const delayMs = remaining > REFRESH_LEAD_SECONDS + 5
      ? (remaining - REFRESH_LEAD_SECONDS) * 1000
      : 0;
    this._refreshTimer = setTimeout(() => {
      this.ensureFreshToken().then((next) => {
        if (!next) this.logout();
      });
    }, delayMs);
  },

  logout() {
    if (this._refreshTimer) {
      clearTimeout(this._refreshTimer);
      this._refreshTimer = null;
    }
    localStorage.removeItem('token');
    // Notify app to transition to login immediately
    window.dispatchEvent(new CustomEvent('auth:unauthorized', {
      detail: { message: 'You have been logged out.' }
    }));
  },

  isAuthenticated() {
    const token = localStorage.getItem('token');
    if (!token) return false;
    if (tokenExp(token) === null && token.split('.').length !== 3) return false;
    return secondsRemaining(token) > 0;
  },

  getToken() {
    const token = localStorage.getItem('token');
    if (!token || !this.isAuthenticated()) return null;
    return token;
  },

  getProjectId() {
    const token = localStorage.getItem('token');
    if (!token) return null;
    try {
      const decoded = jwtDecode(token);

      return decoded.projectId;
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  },

  getAllowedProjectIds() {
    const token = localStorage.getItem('token');
    if (!token) return [];
    try {
      const decoded = jwtDecode(token);
      const raw = decoded.allowedProjectIds;
      if (!raw) return [];

      // allowedProjectIds is stored as JSON string in JWT
      if (Array.isArray(raw)) return raw.map(String);
      const s = String(raw).trim();
      if (!s) return [];

      try {
        const parsed = JSON.parse(s);
        if (Array.isArray(parsed)) return parsed.map(String);
      } catch {
        // ignore
      }

      return s.split(',').map((v) => v.trim()).filter(Boolean);
    } catch (error) {
      console.error('Error decoding allowedProjectIds:', error);
      return [];
    }
  },

  getUserInfo() {
    const token = localStorage.getItem('token');
    if (!token) return null;
    try {
      const decoded = jwtDecode(token);
      return {
        email: decoded.email || decoded.username || decoded.name || null,
        name: decoded.name || decoded.username || decoded.email || null,
        ...decoded
      };
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  }
};

